import { RefObject, useCallback, useMemo, useState } from 'react';
import { useAgentChat } from '../../../contexts/AgentChatContext';
import { useHtmlStepperAgentSessionActions } from './useHtmlStepperAgentSessionActions';
import {
  useHtmlStepperEditorActions,
  UseHtmlStepperEditorActionsReturn,
} from './useHtmlStepperEditorActions';
import {
  useHtmlStepperEditorData,
  UseHtmlStepperEditorDataReturn,
} from './useHtmlStepperEditorData';
import {
  useHtmlStepperEditorModals,
  UseHtmlStepperEditorModalsReturn,
} from './useHtmlStepperEditorModals';
import {
  useHtmlStepperEditorState,
  UseHtmlStepperEditorStateReturn,
} from './useHtmlStepperEditorState';
import {
  useHtmlStepperIframeMessaging,
  UseHtmlStepperIframeMessagingReturn,
} from './useHtmlStepperIframeMessaging';

export interface UseHtmlStepperEditorReturn {
  // State
  state: UseHtmlStepperEditorStateReturn;

  // Modals
  modals: UseHtmlStepperEditorModalsReturn;

  // Data
  data: UseHtmlStepperEditorDataReturn;

  // Actions
  actions: UseHtmlStepperEditorActionsReturn;

  // Iframe messaging
  iframe: UseHtmlStepperIframeMessagingReturn;

  // Agent session actions
  agentActions: ReturnType<typeof useHtmlStepperAgentSessionActions>;

  // Preview ref
  previewRef: RefObject<HTMLIFrameElement | null>;
}

export interface UseHtmlStepperEditorProps {
  quizId: string;
  initialFormId: string;
  previewRef: RefObject<HTMLIFrameElement | null>;
}

export function useHtmlStepperEditorHook({
  quizId,
  initialFormId,
  previewRef,
}: UseHtmlStepperEditorProps): UseHtmlStepperEditorReturn {
  // UI state (device, editor mode, sidebar, selection, selected form)
  const state = useHtmlStepperEditorState({ initialFormId });

  // Modal state
  const modals = useHtmlStepperEditorModals();

  // Agent chat context for immediate running state
  const { getAgentState } = useAgentChat();

  // Form data and agent session
  const data = useHtmlStepperEditorData({
    formId: state.selectedFormId,
    quizId,
    currentStep: state.currentStep,
    device: state.device,
  });

  // Calculate isEditable based on editor mode and agent session state
  // Use getAgentState() for immediate running state (avoids Firestore delay)
  const isEditable = useMemo(() => {
    if (state.editorMode !== 'editor') return false;
    const agentState = getAgentState();
    if (agentState.isStreaming || agentState.isWorking) return false;
    if (data.agentSession.agentHasUnreviewedChanges) return false;
    return true;
  }, [
    state.editorMode,
    getAgentState,
    data.agentSession.agentHasUnreviewedChanges,
  ]);

  // Counter to trigger preview refresh when local form data changes
  const [dataVersion, setDataVersion] = useState(0);
  const incrementDataVersion = useCallback(
    () => setDataVersion((v) => v + 1),
    [],
  );

  // Iframe messaging - uses modal callbacks (created before actions so goToStep is available)
  const iframe = useHtmlStepperIframeMessaging({
    quizId,
    formId: state.selectedFormId || '',
    form: data.form,
    website: data.website,
    setRawForm: data.setRawForm,
    previewRef,
    accountId: data.accountId,
    primaryDomain: data.primaryDomain,
    dynamicPageId: data.dynamicPageId,
    variantId: data.variantId,
    websiteId: data.websiteId,
    onOpenEditHtmlActionModal: modals.onOpenEditHtmlActionModal,
    onOpenEditFieldModal: modals.onOpenEditFieldModal,
    onOpenSelectImageModal: modals.onOpenSelectImageModal,
    onOpenSelectThumbnailModal: modals.onOpenSelectThumbnailModal,
    isVisualEditMode: state.isVisualEditMode,
    setIsVisualEditMode: state.setIsVisualEditMode,
    editorMode: state.editorMode,
    isEditable,
    currentStep: state.currentStep,
    onStepChanged: state.setCurrentStep,
    dataVersion,
  });

  // Step CRUD actions (uses iframe.goToStep for navigation after create/delete/duplicate)
  const actions = useHtmlStepperEditorActions({
    formData: data.formData,
    updateFormData: data.updateFormData,
    currentStep: state.currentStep,
    setCurrentStep: state.setCurrentStep,
    goToStep: iframe.goToStep,
    onDataChanged: incrementDataVersion,
  });

  // Agent session actions
  const agentActions = useHtmlStepperAgentSessionActions({
    formId: state.selectedFormId,
    agentSession: data.agentSession,
    formData: data.formData,
    rawFormData: data.rawFormData,
    updateFormData: data.updateFormData,
    onNavigateToStep: (step) => {
      state.setCurrentStep(step);
      iframe.goToStep(step);
    },
  });

  return {
    state,
    modals,
    data,
    actions,
    iframe,
    agentActions,
    previewRef,
  };
}
